import React, { useCallback, useMemo, useRef } from 'react';
import { ApiAssignmentUser } from '../api';
import { MenuItem, SalvMenuButton, Size } from '@salvapp/ui-components';
import './AlertRowAssignment.scss';
import { useSelector } from 'react-redux';
import { getUserId } from '../auth/authReducer';
import { AppState } from '../store/store';
import Has from '../auth/Has';
import type { AlignDirection } from 'react-bootstrap/types';
import { faUser } from '@fortawesome/pro-solid-svg-icons';
import { useAssignableUsers } from '../users/useAssignableUsers';
import { faUserXmark } from '@fortawesome/pro-solid-svg-icons/faUserXmark';
import useShortcut from '../common/useShortcut';
import { InternalApiUserBasicDetails } from '../api-internal';

export interface InvestigationAssignmentProps {
  assignee?: Omit<ApiAssignmentUser, 'id'>;
  maxSize?: number;
  onChange: (user: InternalApiUserBasicDetails) => void;
  onUnassign?: () => void;
  sizeClass?: string;
  dropdownAlign?: AlignDirection;
  shortcutTriggerKey?: string;
  size?: Size;
  anyPermissions: string[];
}

export const InvestigationAssignmentDropdown = ({
  assignee,
  maxSize = 40,
  onChange,
  onUnassign,
  shortcutTriggerKey,
  size = Size.MEDIUM,
  anyPermissions,
}: InvestigationAssignmentProps) => {
  const { assignableUsers } = useAssignableUsers();
  const currentUserId = useSelector((state: AppState) => getUserId(state));

  const displayedAssignments = useMemo(() => {
    const currentUserIndex = assignableUsers?.findIndex((u) => u.id === currentUserId);
    if (currentUserIndex && currentUserIndex !== -1 && assignableUsers) {
      const currentUser = assignableUsers[currentUserIndex];
      return [currentUser, ...assignableUsers.filter((u) => u.id !== currentUserId)];
    }
    return assignableUsers;
  }, [assignableUsers, currentUserId]);

  const ref = useRef<HTMLButtonElement>(null);
  const openMenu = useCallback(() => {
    ref.current?.click();
  }, [ref]);
  useShortcut({ triggerKey: shortcutTriggerKey, onTrigger: openMenu });

  return (
    <Has anyPermissions={anyPermissions}>
      {(can: boolean) => (
        <SalvMenuButton
          displayLimit={maxSize}
          filterPlaceholder={'Search...'}
          label={assignee?.name ?? 'Assign'}
          icon={assignee ? undefined : faUser}
          avatarEmail={assignee?.email}
          onAction={([value]) => {
            if (value === 'unassign') {
              onUnassign?.();
            } else {
              const selectedUser = displayedAssignments?.find((user) => user.id!.toString() === value);

              if (selectedUser) {
                onChange(selectedUser);
              }
            }
          }}
          enableFilter
          size={size}
          disabled={!can}
          placement={'bottom right'}
          ref={ref}
        >
          {onUnassign && assignee && <MenuItem title={'Unassign'} icon={faUserXmark} value={'unassign'} sectionEnd />}
          {displayedAssignments?.map((user) => (
            <MenuItem
              key={user.email}
              title={user.name}
              subtitle={user.email}
              avatarEmail={user.email}
              value={user.id!.toString()}
            />
          ))}
        </SalvMenuButton>
      )}
    </Has>
  );
};

export default InvestigationAssignmentDropdown;
